import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router-dom';
import {
    PrimaryButton,
    SecondaryButton,
    PageHeader,
    FieldLayoutSmall,
    LinkButton,
    icons as ICONS,
    Alert
} from 'cstar-react-primitives';

import originRedirect from '../../utils/originRedirect';
import { confirmationPath } from '../paths/utils';
import OrderProvider, { useOrder } from '../../mapi/OrderProvider';
import { useFormatMessage } from '../../intl/intlHooks';
import { useSettings } from '../../mall_settings/SettingsProvider';
import { dispatchCartUpdateWindowEvent } from '../../utils/browser';
import {
    getTotalFees,
    getGrandTotal,
    getTotalQuantity,
    getTotalShipping,
    getTotalValue,
    getFeeTotalQuantity,
    selectLineItems,
} from '../../lineItem/lineItemSelectors';

import { cashbotName } from '../core';
import PaymentMethod from '../payment/PaymentMethod';
import SelectPaymentMenu from '../payment/SelectPaymentMenu';
import { PaymentMethodProvider, usePaymentMethods } from '../payment/PaymentMethodProvider';

import LoginOptionsTab from './LoginOptionsTab';
import { CartTotalItems } from './CartTotalItems';
import { EmptyOrderSummary } from './EmptyOrderSummary';
import { OrderSummaryTotals } from './OrderSummaryTotals';
import { OrderSummaryDetails } from './OrderSummaryDetails';
import { orderSummaryMessages as msgs } from './orderSummaryMessages';
import { creditCardFormMessages } from '../payment/methods/creditCard/creditCardFormMessages';
import {ApplyPromosProvider, useApplyPromos} from "../../mapi/ApplyPromosProvider";

export const OrderSummary = ({ totalValue, lineItemsTotalQuantity, lineItemsTotalShipping, lineItemsTotalFees, lineItems, feeQuantity }) => {
    const { userPoints, authentication } = useSettings();
    const { statement, orderNumber, orderItems, sanitizedPayment, orderHasPromoError,
        orderOverDailyLimit, orderStatusSuccess, orderStatusProcessing, orderHasStatusError,
        orderOverPromoRedemptionLimit, orderMissingPaymentInfo} = useOrder();
    const { hasMultiplePaymentMethods } = usePaymentMethods();
    const formatMessage = useFormatMessage();
    const stepDivRef = useRef(React.createRef());
    const [currentStep, setCurrentStep] = useState('summary');
    const { promoDiscounts, applyPromos } = useApplyPromos();

    dispatchCartUpdateWindowEvent(lineItemsTotalQuantity);

    useEffect(() => {
        
        if (!orderStatusProcessing) {
            applyPromos(lineItems, totalValue);
        }
    }, [lineItemsTotalQuantity, orderStatusProcessing]);

    useEffect(() => {
        if (orderHasStatusError || orderStatusProcessing) {
            stepDivRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [orderHasStatusError, orderStatusProcessing]);

    const getAlertMessage = () => {
        if (orderOverDailyLimit) {
            return msgs.orderOverDailyLimit;
        }
        if (orderHasPromoError) {
            return msgs.orderPromosMayHaveChanged;
        }
        if (orderOverPromoRedemptionLimit) {
            return msgs.orderOverPromoRedemptionLimit;
        }
        if (orderMissingPaymentInfo){
            return msgs.orderMissingPaymentInfo;
        }
        return (hasMultiplePaymentMethods ? msgs.multiplePaymentMethodError : msgs.singlePaymentMethodError);
    };

    useEffect(() => {
        
        stepDivRef.current.scrollIntoView();
    }, [currentStep]);

    const continueButtonProps = {
        type: 'button',
        onClick: originRedirect,
        cashbotName
    };
    const orderSummaryTotalsProps = {
        promoTotal: promoDiscounts.totalDiscountRewardAmount,
        subTotal: totalValue,
        quantity: lineItemsTotalQuantity,
        shipping: lineItemsTotalShipping,
        totalFees: lineItemsTotalFees,
        feeQuantity
    };
    const mobileContinueButtonProps = {
        type: 'button',
        btnText: formatMessage(msgs.continueShoppingButtonText),
        onClick: originRedirect,
        isSmall: true,
        cashbotName
    };
    const mobileCheckOutButtonProps = {
        type: 'submit',
        btnText: formatMessage(msgs.checkOutButtonText),
        onClick: () => setCurrentStep('payment'),
        isSmall: true,
        cashbotName
    };
    const summaryButtonProps = {
        type: 'submit',
        btnText: formatMessage(msgs.backButtonText),
        onClick: () => setCurrentStep('summary'),
        cashbotName
    };

    if (orderStatusSuccess) {
        return (
            <Redirect
                to={{
                    pathname: confirmationPath,
                    state: { lineItems: orderItems, payment: sanitizedPayment, orderNumber, statement, promoDiscounts }
                }}
            />
        );
    }

    return lineItemsTotalQuantity < 1 ? (
        <div ref={stepDivRef}>
            <EmptyOrderSummary />
        </div>
    ) : (
        <div className="mall-box">
            <div className={`${currentStep}-step`} ref={stepDivRef}>
                <div className="column-wrap">
                    <div className="primary-column cart-column">
                        <CartTotalItems quantity={lineItemsTotalQuantity} />
                        <div className="continue-shopping-button">
                            <LinkButton {...continueButtonProps}>
                                <FormattedMessage {...msgs.continueShoppingButtonText} />
                                {ICONS.CHEVRON_RIGHT}
                            </LinkButton>
                        </div>
                        <OrderSummaryDetails />
                        {!userPoints && <OrderSummaryTotals {...orderSummaryTotalsProps} />}
                        <div className="mobile-controls">
                            <SecondaryButton {...mobileContinueButtonProps} />
                            <PrimaryButton {...mobileCheckOutButtonProps} />
                        </div>
                    </div>
                    <div className="secondary-column payment-column">
                        {orderHasStatusError && (
                            <div>
                                <Alert alertType="error" hasHeading={false} message={getAlertMessage()} />
                            </div>
                        )}
                        {orderStatusProcessing && (
                            <p className="checkout-order-pending">
                                <FormattedMessage {...creditCardFormMessages.placingOrder} />
                            </p>
                        )}
                        <FieldLayoutSmall isCentered>
                            <div className="mobile-controls">
                                <PrimaryButton {...summaryButtonProps} />
                            </div>
                            <PageHeader heading={formatMessage(creditCardFormMessages.checkoutTitle)} />

                            {hasMultiplePaymentMethods && <SelectPaymentMenu />}
                            {authentication === 'optional' ? (
                                <LoginOptionsTab>
                                    <PaymentMethod totalValue={totalValue} />
                                </LoginOptionsTab>
                            ) : (
                                <PaymentMethod totalValue={totalValue} />
                            )}
                        </FieldLayoutSmall>
                    </div>
                </div>
            </div>
        </div>
    );
};

OrderSummary.propTypes = {
    totalValue: PropTypes.number.isRequired,
    lineItemsTotalQuantity: PropTypes.number.isRequired,
    lineItemsTotalShipping: PropTypes.number.isRequired,
    lineItemsTotalFees: PropTypes.number.isRequired,
    lineItems: PropTypes.array.isRequired,
    feeQuantity: PropTypes.number.isRequired
};

const mapStateToProps = state => ({
    totalValue: getTotalValue(state),
    lineItemsTotalQuantity: getTotalQuantity(state),
    lineItemsTotalShipping: getTotalShipping(state),
    lineItemsTotalFees: getTotalFees(state),
    amount: getGrandTotal(state),
    lineItems: selectLineItems(state),
    feeQuantity: getFeeTotalQuantity(state)
});

export default connect(mapStateToProps)(props => (
    <PaymentMethodProvider>
        <ApplyPromosProvider {...props}>
            <OrderProvider>
                <OrderSummary {...props} />
            </OrderProvider>
        </ApplyPromosProvider>
    </PaymentMethodProvider>
));
